/**
 * OZK 도움연결 (P0.5, ③A) customer-facing copy + AI/admin contract.
 *
 * Source of truth: docs/OZK_HELP_CONNECTION_CUSTOMER_COPY_AND_CODEX_SPEC.md.
 * This file is the "Codex 구현 계약" half of that document made real: the
 * category list, risk levels, and canned copy templates that
 * functions/api/help-requests/* and client/src/admin/HelpRequestsManagement.tsx
 * actually import, instead of the spec staying documentation-only.
 *
 * Core honesty rule (spec's "문체의 핵심"): never claim a business has lots
 * of experience, is "the best", or "definitely solves it" unless that's
 * backed by real completed-match data. We don't have completed-match data
 * yet (help_request_matches has no "completed" outcome tracking wired to a
 * business yet), so hasVerifiedExperience should stay false everywhere in
 * the codebase until that changes - do not flip it to true as a shortcut.
 */

export type HelpConnectionRiskLevel = "R1" | "R2" | "R3" | "R4";

export type HelpConnectionCategoryId =
  | "accounting_tax"
  | "immigration_visa"
  | "legal"
  | "wage_super"
  | "rental"
  | "real_estate"
  | "moving"
  | "cleaning"
  | "repair"
  | "car"
  | "education"
  | "childcare"
  | "medical"
  | "finance"
  | "food_catering"
  | "translation_admin"
  | "family_emergency";

export interface HelpConnectionCategoryDef {
  id: HelpConnectionCategoryId;
  /** Korean display label used in admin UI and Topic taxonomy cross-references. */
  label: string;
  /** Provider noun used inside copy templates, e.g. "회계사님". */
  providerNoun: string;
  riskLevel: HelpConnectionRiskLevel;
  /**
   * Eventual target for the phased rollout (12장 반자동 전환) once enough
   * completed matches exist to trust rule-based auto-matching - NOT the
   * current MVP behavior. Every category is manually matched by an admin
   * today regardless of this flag (see requireHelpRequestAdmin/[id]/match.ts).
   */
  autoMatchAllowed: boolean;
  humanReviewRequired: boolean;
}

export const HELP_CONNECTION_CATEGORIES: HelpConnectionCategoryDef[] = [
  { id: "accounting_tax", label: "회계·세금", providerNoun: "회계사", riskLevel: "R2", autoMatchAllowed: false, humanReviewRequired: true },
  { id: "immigration_visa", label: "이민·비자", providerNoun: "이민 컨설턴트", riskLevel: "R4", autoMatchAllowed: false, humanReviewRequired: true },
  { id: "legal", label: "법률", providerNoun: "변호사", riskLevel: "R4", autoMatchAllowed: false, humanReviewRequired: true },
  { id: "wage_super", label: "임금·Super", providerNoun: "노무 상담사", riskLevel: "R3", autoMatchAllowed: false, humanReviewRequired: true },
  { id: "rental", label: "렌트", providerNoun: "임대차 상담사", riskLevel: "R3", autoMatchAllowed: false, humanReviewRequired: true },
  { id: "real_estate", label: "부동산", providerNoun: "부동산 에이전트", riskLevel: "R2", autoMatchAllowed: false, humanReviewRequired: true },
  { id: "moving", label: "이사", providerNoun: "이사업체", riskLevel: "R1", autoMatchAllowed: true, humanReviewRequired: false },
  { id: "cleaning", label: "청소", providerNoun: "청소업체", riskLevel: "R1", autoMatchAllowed: true, humanReviewRequired: false },
  { id: "repair", label: "수리", providerNoun: "수리 기사", riskLevel: "R1", autoMatchAllowed: true, humanReviewRequired: false },
  { id: "car", label: "자동차", providerNoun: "카센터", riskLevel: "R1", autoMatchAllowed: true, humanReviewRequired: false },
  { id: "education", label: "교육", providerNoun: "유학원·과외 선생님", riskLevel: "R2", autoMatchAllowed: false, humanReviewRequired: true },
  { id: "childcare", label: "Childcare", providerNoun: "Childcare 상담사", riskLevel: "R2", autoMatchAllowed: false, humanReviewRequired: true },
  { id: "medical", label: "의료", providerNoun: "의료 통역·안내 담당자", riskLevel: "R4", autoMatchAllowed: false, humanReviewRequired: true },
  { id: "finance", label: "금융", providerNoun: "재정 상담사", riskLevel: "R3", autoMatchAllowed: false, humanReviewRequired: true },
  { id: "food_catering", label: "음식·케이터링", providerNoun: "케이터링 업체", riskLevel: "R1", autoMatchAllowed: true, humanReviewRequired: false },
  { id: "translation_admin", label: "번역·행정", providerNoun: "번역·행정대행 담당자", riskLevel: "R2", autoMatchAllowed: false, humanReviewRequired: true },
  { id: "family_emergency", label: "가족 긴급지원", providerNoun: "가족 긴급지원 담당자", riskLevel: "R4", autoMatchAllowed: false, humanReviewRequired: true },
];

export function getHelpConnectionCategory(
  id: string | null | undefined,
): HelpConnectionCategoryDef | undefined {
  return HELP_CONNECTION_CATEGORIES.find((category) => category.id === id);
}

export const HELP_CONNECTION_BADGES = {
  foundingPartner: "OZK Founding Partner",
  koreanSpeaking: "한국어 상담 가능",
  verifiedRegistration: "등록정보 확인",
} as const;

/** 공통 대화 순서 (spec): 공감 → 상황 확인 → 첫 행동 → 업체 제안 → 고객 선택 → 전달 동의 → 진행 확인 */
export const HELP_CONNECTION_FLOW_STEPS = [
  "empathize",
  "clarify_situation",
  "first_action",
  "recommend_business",
  "customer_selects",
  "consent_to_forward",
  "progress_check",
] as const;

export const helpConnectionGreetingCopy = {
  opening:
    "안녕하세요, OZK입니다. 말씀해주신 내용 잘 확인했어요. 지금 상황을 조금 더 정확히 알수록 더 도움이 되는 안내를 드릴 수 있어요.",
  clarifyPrompt:
    "어느 지역에 계신지, 그리고 지금까지 어떤 조치를 하셨는지 알려주시겠어요?",
};

/** 첫 행동 안내는 카테고리별 safeFirstResponse가 있으면 그것을 우선 사용하고, 없으면 이 문구로 대체한다. */
export const helpConnectionFallbackFirstAction =
  "우선 관련 공식 절차나 확인해야 할 서류가 있는지부터 함께 살펴볼게요. 확실한 답을 드리기 전에는 추측성 안내를 드리지 않습니다.";

/**
 * 업체 추천 문구. hasVerifiedExperience는 실제 완료된 연결 데이터로만 true가
 * 되어야 한다 (지금은 어떤 카테고리도 이 데이터가 없으므로 항상 false).
 */
export function buildRecommendationCopy(opts: {
  categoryId: HelpConnectionCategoryId;
  hasVerifiedExperience: boolean;
}): string {
  const category = getHelpConnectionCategory(opts.categoryId);
  const noun = category?.providerNoun || "전문가";
  return opts.hasVerifiedExperience
    ? `이런 문제를 자주 상담해 온 ${noun}님이 계세요. 원하시면 지금 말씀하신 내용을 간단히 정리해서 연결해드릴까요?`
    : `이 분야를 한국어로 상담할 수 있는 ${noun}님이 있습니다. 필요하시면 상담 가능 여부를 확인해드릴게요.`;
}

export const helpConnectionConsentCopy = {
  askConsent:
    "말씀해주신 내용을 업체에 전달해서 상담을 이어가시겠어요? 전달 전에는 이름과 연락처가 업체에 넘어가지 않습니다.",
  consentGranted: "동의해주셔서 감사합니다. 지금 업체에 전달할게요.",
  consentDeclined:
    "알겠습니다. 업체 전달 없이 이 안내만 참고해주세요. 마음이 바뀌면 언제든 다시 말씀해주세요.",
};

export const helpConnectionOutcomeCopy = {
  businessAccepted: (businessName: string) =>
    `${businessName}에서 상담을 진행할 수 있다고 확인했습니다. 곧 연락드릴 예정이니 조금만 기다려주세요.`,
  businessDeclined:
    "안내드린 업체에서 지금은 상담이 어렵다고 회신했습니다. 다른 업체를 다시 찾아볼게요.",
  noResponse:
    "업체 응답이 조금 늦어지고 있어요. 계속 확인 중이니 조금만 더 기다려주시고, 급하신 경우 다시 말씀해주세요.",
  followUpCheck:
    "안내드린 내용은 잘 진행되고 계신가요? 추가로 필요한 부분이 있으면 언제든 다시 문의해주세요.",
};

/** functions/api/help-requests/[id]/match.ts가 business_inquiries.message에 넣는 문구의 기반. */
export function buildBusinessForwardCopy(opts: {
  referenceCode: string;
  summary: string;
}): string {
  return `[OZK 도움연결 ${opts.referenceCode}] ${opts.summary}`;
}

/** 업체가 고객에게 답할 때 안내하는 권장·금지 문구 (관리자/업체 온보딩 화면에 노출). */
export const helpConnectionBusinessReplyGuidance = {
  recommended: [
    "먼저 상황을 다시 한 번 확인하고, 확실하지 않은 부분은 확인 후 답변드린다고 안내하세요.",
    "상담 가능 여부와 예상 소요 시간을 구체적으로 안내하세요.",
    "OZK를 통해 연결된 고객임을 밝히고, 필요한 서류나 정보를 명확히 요청하세요.",
  ],
  prohibited: [
    "확인되지 않은 법률·의료·비자 결과를 단정적으로 약속하지 마세요.",
    "OZK를 거치지 않은 별도 결제나 개인 계좌 송금을 요구하지 마세요.",
    "고객의 개인정보를 본 상담 목적 외로 사용하지 마세요.",
  ],
};

/** 관리자가 자동분류 없이 수동으로 응대할 때 사용하는 기본 문구 (12장 admin 큐 "기본 답변 보내기"). */
export const helpConnectionAdminManualReplyCopy = {
  needsMoreInfo:
    "질문 감사합니다. 조금 더 정확한 안내를 위해 지역과 지금까지 진행하신 내용을 알려주시겠어요?",
  guidanceSent:
    "말씀해주신 내용을 확인했습니다. 관련해서 도와드릴 수 있는 업체를 찾아볼게요. 잠시만 기다려주세요.",
};

/**
 * AI 응답 JSON 계약 (spec's "AI 응답 JSON 계약과 TypeScript 구조"). 아직 실제
 * AI 분류기는 연결돼 있지 않다 - 이 타입은 12장/16장이 말하는 반자동 전환
 * 단계에서 functions 쪽에 AI 호출을 추가할 때 그대로 쓸 계약이다.
 */
export interface HelpConnectionAiSuggestion {
  categoryId: HelpConnectionCategoryId | "unknown";
  riskLevel: HelpConnectionRiskLevel;
  /** 0-1, AI 분류 확신도. 낮으면 관리자 수동 검토로 보낸다. */
  confidence: number;
  suggestedFollowUpQuestions: string[];
  safeFirstResponse: string;
  recommendedBusinessCategory?: string;
  handoffMessage: string;
  autoMatchAllowed: boolean;
  humanReviewRequired: boolean;
}

/**
 * Premium 업체가 관련성 필터를 우회해서 부적합한 문의까지 받지 못하도록 하는
 * 매칭 규칙. 실제 매칭 로직(현재는 관리자가 수동으로 businessId를 고르는
 * functions/api/help-requests/[id]/match.ts)이 이 규칙을 강제해야 한다 -
 * Premium 여부와 무관하게 카테고리·지역이 맞지 않으면 후보에서 제외한다.
 */
export const helpConnectionMatchingFairnessRule =
  "Premium/Founding Partner 등급은 같은 카테고리·지역 조건을 만족하는 후보 중 노출 우선순위만 높일 수 있다. 카테고리나 지역이 맞지 않는 업체를 등급만으로 후보에 끼워 넣을 수 없다.";
